import { useState } from 'react'
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts'
import type { CollectionStrategy } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

interface StrategyData {
  strategy: CollectionStrategy
  label: string
  color: string
  latency_p50: number
  latency_p95: number
  latency_p99: number
  coverage: number
  completeness: number
  bandwidth_kb_per_atm_day: number
  duplicate_rate: number
  cpu_overhead: number
  description: string
  pros: string[]
  cons: string[]
  recommended: boolean
}

// API response shapes from collection-api /api/v1/collect/benchmark
interface APIStrategyMetrics {
  strategy: string
  avg_latency_ms: number
  p95_latency_ms: number
  p99_latency_ms: number
  events_per_second: number
  coverage_pct: number
  missed_events: number
  duplicate_events: number
  bandwidth_kbps: number
  notes: string
}

interface APIBenchmarkResult {
  fleet_size: number
  results: APIStrategyMetrics[]
  winner: string
  recommendation: string
}

const API_TO_STRATEGY_KEY: Record<string, CollectionStrategy> = {
  snmp_polling: 'snmp',
  mqtt_push: 'mqtt',
  hybrid: 'hybrid',
  xfs_journal_pull: 'xfs',
  iso8583_tap: 'opex',
}

const STATIC_STRATEGIES: StrategyData[] = [
  {
    strategy: 'snmp', label: 'SNMP Traps', color: '#6b7280',
    latency_p50: 450, latency_p95: 2800, latency_p99: 8200,
    coverage: 72, completeness: 61, bandwidth_kb_per_atm_day: 180,
    duplicate_rate: 12, cpu_overhead: 8,
    description: 'Protocolo estándar de red. Simple pero limitado en cobertura de fallos XFS.',
    pros: ['Amplio soporte de hardware', 'Bajo overhead', 'Fácil implementación'],
    cons: ['Cobertura limitada 61%', 'Alta tasa de duplicados', 'Sin contexto de transacción'],
    recommended: false,
  },
  {
    strategy: 'mqtt', label: 'MQTT Broker', color: '#a78bfa',
    latency_p50: 120, latency_p95: 380, latency_p99: 890,
    coverage: 85, completeness: 78, bandwidth_kb_per_atm_day: 320,
    duplicate_rate: 4, cpu_overhead: 15,
    description: 'Messaging ligero basado en publish/subscribe. Buena latencia, requiere broker.',
    pros: ['Baja latencia', 'QoS configurable', 'Soporte IoT nativo'],
    cons: ['Requiere broker dedicado', 'Alto CPU', 'Cobertura media'],
    recommended: false,
  },
  {
    strategy: 'opex', label: 'ISO 8583 TAP', color: '#facc15',
    latency_p50: 5, latency_p95: 22, latency_p99: 45,
    coverage: 75, completeness: 65, bandwidth_kb_per_atm_day: 95,
    duplicate_rate: 1, cpu_overhead: 5,
    description: 'Tapping de mensajes ISO 8583 en switch de red. Ultra-baja latencia, solo transacciones.',
    pros: ['Latencia sub-10ms', 'Cero agente en ATM', 'Bajo CPU'],
    cons: ['Solo cubre transacciones', 'Sin fallos XFS/hardware', 'Requiere acceso de red'],
    recommended: false,
  },
  {
    strategy: 'xfs', label: 'XFS Journal Pull', color: '#02AFF0',
    latency_p50: 400, latency_p95: 1150, latency_p99: 2250,
    coverage: 97, completeness: 93, bandwidth_kb_per_atm_day: 120,
    duplicate_rate: 3, cpu_overhead: 10,
    description: 'Pull periódico de journals XFS desde el ATM. Alta completitud y calidad de auditoría.',
    pros: ['Cobertura >96%', 'Alta completitud de auditoría', 'Estándar abierto'],
    cons: ['Latencia alta (pull interval)', 'Requiere XFS runtime', 'No apto para alertas en tiempo real'],
    recommended: false,
  },
  {
    strategy: 'hybrid', label: 'Hybrid (XFS+SNMP)', color: '#F89421',
    latency_p50: 95, latency_p95: 240, latency_p99: 620,
    coverage: 98, completeness: 96, bandwidth_kb_per_atm_day: 310,
    duplicate_rate: 3, cpu_overhead: 18,
    description: 'Combina XFS para eventos críticos con SNMP para health monitoring. Óptimo para flotas mixtas.',
    pros: ['Cobertura 98% — máxima', 'Latencia excelente', 'Tolerante a fallos', 'Soporte GRG + NCR'],
    cons: ['Complejidad de integración', 'Deduplicación requerida'],
    recommended: true,
  },
]

function mergeAPIResults(apiResults: APIStrategyMetrics[], base: StrategyData[]): StrategyData[] {
  const byStrategy = new Map(base.map((s) => [s.strategy, { ...s }]))
  for (const r of apiResults) {
    const key = API_TO_STRATEGY_KEY[r.strategy]
    if (!key) continue
    const existing = byStrategy.get(key)
    if (!existing) continue
    const totalEvents = Math.max(r.events_per_second * 10, 1)
    byStrategy.set(key, {
      ...existing,
      latency_p50: Math.round(r.avg_latency_ms),
      latency_p95: Math.round(r.p95_latency_ms),
      latency_p99: Math.round(r.p99_latency_ms),
      coverage: Math.round(r.coverage_pct * 10) / 10,
      completeness: Math.round(r.coverage_pct * 0.97 * 10) / 10,
      bandwidth_kb_per_atm_day: Math.round(r.bandwidth_kbps * 10.8 * 10) / 10,
      duplicate_rate: Math.round((r.duplicate_events / totalEvents) * 100 * 10) / 10,
    })
  }
  return [...byStrategy.values()]
}

const RADAR_METRICS = [
  { key: 'coverage_n', label: 'Cobertura' },
  { key: 'completeness_n', label: 'Completitud' },
  { key: 'latency_n', label: 'Baja Latencia' },
  { key: 'bandwidth_n', label: 'Eficiencia BW' },
  { key: 'cpu_n', label: 'Bajo CPU' },
]

function normalizeStrategy(s: StrategyData) {
  return {
    ...s,
    coverage_n: s.coverage,
    completeness_n: s.completeness,
    latency_n: 100 - Math.min(100, (s.latency_p95 / 5000) * 100),
    bandwidth_n: 100 - Math.min(100, (s.bandwidth_kb_per_atm_day / 500) * 100),
    cpu_n: 100 - s.cpu_overhead,
  }
}

const COLLECTION_API = import.meta.env.VITE_COLLECTION_API_URL || 'http://localhost:8002'

export default function StrategyLab() {
  const C = useTheme()
  const [selected, setSelected] = useState<CollectionStrategy[]>(['snmp', 'xfs', 'hybrid'])
  const [running, setRunning] = useState(false)
  const [benchResult, setBenchResult] = useState<string | null>(null)
  const [liveData, setLiveData] = useState<StrategyData[] | null>(null)
  const [isLive, setIsLive] = useState(false)

  const displayStrategies = liveData ?? STATIC_STRATEGIES

  const radarData = RADAR_METRICS.map(({ key, label }) => {
    const entry: Record<string, unknown> = { metric: label }
    displayStrategies.filter((s) => selected.includes(s.strategy)).forEach((s) => {
      const norm = normalizeStrategy(s)
      entry[s.label] = (norm as unknown as Record<string, number>)[key]
    })
    return entry
  })

  const latencyData = displayStrategies.filter((s) => selected.includes(s.strategy)).map((s) => ({
    name: s.label, p50: s.latency_p50, p95: s.latency_p95, p99: s.latency_p99, color: s.color,
  }))

  const runBenchmark = async () => {
    setRunning(true)
    setBenchResult(null)
    try {
      const res = await fetch(
        `${COLLECTION_API}/api/v1/collect/benchmark?fleet_size=100&duration_seconds=10&seed=42`
      )
      if (res.ok) {
        const data: APIBenchmarkResult = await res.json()
        const merged = mergeAPIResults(data.results, STATIC_STRATEGIES)
        setLiveData(merged)
        setIsLive(true)
        const winnerKey = API_TO_STRATEGY_KEY[data.winner]
        const winnerLabel = merged.find((s) => s.strategy === winnerKey)?.label ?? data.winner
        setBenchResult(`Benchmark completado — ganador: ${winnerLabel}. ${data.recommendation}`)
      } else {
        setBenchResult('API no disponible. Mostrando datos simulados.')
      }
    } catch {
      setBenchResult('API no disponible en modo demo. Los datos mostrados son simulados.')
    }
    setRunning(false)
  }

  const exportResults = () => {
    const csv = [
      ['Estrategia', 'P50 ms', 'P95 ms', 'P99 ms', 'Cobertura %', 'Completitud %', 'BW KB/ATM/día', 'Duplicados %', 'CPU %'],
      ...displayStrategies.map((s) => [s.label, s.latency_p50, s.latency_p95, s.latency_p99, s.coverage, s.completeness, s.bandwidth_kb_per_atm_day, s.duplicate_rate, s.cpu_overhead]),
    ].map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'strategy_comparison.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const toggleStrategy = (s: CollectionStrategy) => {
    setSelected((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16, background: C.dark, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Strategy Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {displayStrategies.map((s) => (
          <div
            key={s.strategy}
            onClick={() => toggleStrategy(s.strategy)}
            style={{
              background: selected.includes(s.strategy) ? `${s.color}12` : C.card,
              border: `1px solid ${selected.includes(s.strategy) ? s.color : C.border}`,
              borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
              position: 'relative', transition: 'all 0.2s',
            }}
          >
            {s.recommended && (
              <span style={{
                position: 'absolute', top: -1, right: 10,
                background: C.orange, color: '#4a2a07', fontSize: 9, fontWeight: 700,
                padding: '2px 8px', borderRadius: '0 0 6px 6px', letterSpacing: 0.5,
              }}>
                RECOMENDADO
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.label}</span>
              {isLive && (
                <span style={{ marginLeft: 'auto', fontSize: 9, color: C.green, fontWeight: 600, letterSpacing: 0.5 }}>LIVE</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{s.description}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', fontSize: 11 }}>
              <Metric label="P95" value={`${s.latency_p95}ms`} color={s.latency_p95 < 500 ? C.green : s.latency_p95 < 2000 ? C.orange : C.red} />
              <Metric label="Cobertura" value={`${s.coverage}%`} color={s.coverage >= 95 ? C.green : s.coverage >= 80 ? C.orange : C.muted} />
              <Metric label="Completitud" value={`${s.completeness}%`} color={s.completeness >= 90 ? C.green : s.completeness >= 75 ? C.orange : C.muted} />
              <Metric label="CPU" value={`${s.cpu_overhead}%`} color={s.cpu_overhead <= 10 ? C.green : s.cpu_overhead <= 18 ? C.orange : C.red} />
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation Banner */}
      <div style={{ background: `${C.orange}12`, border: `1px solid ${C.orange}40`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>★</span>
        <div>
          <div style={{ fontWeight: 700, color: C.orange, fontSize: 13, marginBottom: 4 }}>
            Estrategia Recomendada: Hybrid (XFS + SNMP)
          </div>
          <div style={{ fontSize: 12, color: C.text }}>
            Para la flota mixta Banreservas (NCR + GRG, 933 ATMs), la estrategia Hybrid ofrece 98% de cobertura con latencia p95 de 240ms. XFS provee contexto completo de fallos de dispositivo, mientras SNMP cubre health monitoring de red con bajo overhead. Soporta los 14 modelos de la flota sin agente propietario.
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Radar */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
              Comparación Multi-Dimensión
            </span>
            {isLive && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: 0.5 }}>● LIVE</span>}
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
              <PolarGrid stroke={C.border} />
              <PolarAngleAxis dataKey="metric" tick={{ fill: C.muted, fontSize: 11 }} />
              {displayStrategies.filter((s) => selected.includes(s.strategy)).map((s) => (
                <Radar key={s.strategy} name={s.label} dataKey={s.label}
                  stroke={s.color} fill={s.color} fillOpacity={0.12} strokeWidth={2} />
              ))}
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Latency Comparison */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
              Comparación de Latencia (ms)
            </span>
            {isLive && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: 0.5 }}>● LIVE</span>}
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={latencyData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 10 }} />
              <YAxis tick={{ fill: C.muted, fontSize: 11 }} unit="ms" />
              <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`${v}ms`]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="p50" name="P50" radius={[2, 2, 0, 0]}>
                {latencyData.map((d, i) => <Cell key={i} fill={d.color} opacity={0.5} />)}
              </Bar>
              <Bar dataKey="p95" name="P95" radius={[2, 2, 0, 0]}>
                {latencyData.map((d, i) => <Cell key={i} fill={d.color} opacity={0.75} />)}
              </Bar>
              <Bar dataKey="p99" name="P99" radius={[2, 2, 0, 0]}>
                {latencyData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={runBenchmark} disabled={running} style={{
          background: running ? 'rgba(38,78,114,0.4)' : C.primary, color: '#fff',
          border: `1px solid ${C.blue}`, borderRadius: 7, padding: '8px 18px',
          cursor: running ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
        }}>
          {running ? 'Ejecutando benchmark...' : 'Ejecutar Benchmark'}
        </button>
        <button onClick={exportResults} style={{
          background: 'rgba(2,175,240,0.1)', color: C.blue, border: `1px solid rgba(2,175,240,0.4)`,
          borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
        }}>
          Exportar Resultados
        </button>
        {isLive && (
          <button onClick={() => { setLiveData(null); setIsLive(false); setBenchResult(null) }} style={{
            background: 'transparent', color: C.muted, border: `1px solid ${C.border}`,
            borderRadius: 7, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
          }}>
            Resetear a simulado
          </button>
        )}
        {benchResult && (
          <span style={{ fontSize: 12, color: isLive ? C.green : C.muted, fontStyle: 'italic', maxWidth: 500 }}>{benchResult}</span>
        )}
      </div>

      {/* Detail Table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
            Tabla Comparativa Completa
          </span>
          {isLive && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, letterSpacing: 0.5 }}>● DATOS EN VIVO</span>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Estrategia', 'P50', 'P95', 'P99', 'Cobertura', 'Completitud', 'BW/ATM/día', 'Duplicados', 'CPU'].map((h) => (
                <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Estrategia' ? 'left' : 'right', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayStrategies.map((s) => (
              <tr key={s.strategy} style={{ borderBottom: `1px solid ${C.border}`, background: s.recommended ? `${C.orange}08` : 'transparent' }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                    <span style={{ color: C.text, fontWeight: s.recommended ? 700 : 400 }}>{s.label}</span>
                    {s.recommended && <span style={{ fontSize: 9, color: C.orange }}>★</span>}
                  </div>
                </td>
                {[`${s.latency_p50}ms`, `${s.latency_p95}ms`, `${s.latency_p99}ms`, `${s.coverage}%`, `${s.completeness}%`, `${s.bandwidth_kb_per_atm_day} KB`, `${s.duplicate_rate}%`, `${s.cpu_overhead}%`].map((v, i) => (
                  <td key={i} style={{ padding: '8px 10px', textAlign: 'right', color: C.muted, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  const C = useTheme()
  return (
    <>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ color, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </>
  )
}
